import { LabDashboardEventSummary, LabSummaryMap } from '@/src/types/LabDashboard';
import { formatDateKey } from './calendar.utils';

/**
 * Represents a single row in the daily summary table.
 * Each row corresponds to one event with its aggregated status counts.
 */
export interface AggregatedEventRow {
  /** Route identifier for the event (used in links) */
  eventName: string;
  /** Display title of the event */
  eventTitle: string;
  /** Whether this event is a campaign event */
  campaignEvent: boolean;
  /** Aggregated status counts: sum of all challenge statuses for this event */
  statusCounts: Record<string, number>;
  /** Per-challenge breakdown: challengeId → status → count */
  challengeBreakdown: Record<string, Record<string, number>>;
  /** Set on child (expanded) rows to indicate this is a per-challenge row */
  challengeId?: string;
}

/**
 * Filters calendar events to those ongoing on the given date.
 * An event is ongoing if date ∈ [startDate, endDate] (inclusive),
 * using local-timezone date comparison.
 *
 * Events with null startDate, endDate, or eventName are excluded.
 * Events with no labSummary are still included — they render 0's for every
 * status column so the day's drill-down accounts for every ongoing event.
 * The original array order is preserved.
 */
export const getOngoingEvents = (
  date: Date,
  calendarEvents: LabDashboardEventSummary[]
): LabDashboardEventSummary[] => {
  const dateStr = formatDateKey(date);

  return calendarEvents.filter((event) => {
    if (!event.startDate || !event.endDate || !event.eventName) {
      return false;
    }
    // Parse ISO strings to get local-timezone dates for correct day comparison
    const start = formatDateKey(new Date(event.startDate));
    const end = formatDateKey(new Date(event.endDate));
    return dateStr >= start && dateStr <= end;
  });
};

/**
 * Aggregates a single event's labSummary into a flat status-count map.
 * Sums counts across all challenges for each status key.
 *
 * Returns an empty record if labSummary is null.
 * Treats non-numeric or negative values as 0.
 */
export const aggregateLabSummary = (labSummary: LabSummaryMap | null): Record<string, number> => {
  if (!labSummary) return {};

  const aggregated: Record<string, number> = {};

  for (const challengeId of Object.keys(labSummary)) {
    const statusMap = labSummary[challengeId];
    for (const [status, count] of Object.entries(statusMap)) {
      const safeCount = typeof count === 'number' && count >= 0 ? count : 0;
      aggregated[status] = (aggregated[status] ?? 0) + safeCount;
    }
  }

  return aggregated;
};

/**
 * Transforms filtered events into table row data with aggregated statuses.
 * Each row contains the event name and aggregated status counts.
 * Order matches input event order.
 */
export const buildTableRows = (events: LabDashboardEventSummary[]): AggregatedEventRow[] => {
  return events.map((event) => ({
    eventName: event.eventName as string,
    eventTitle: event.eventTitle || (event.eventName as string),
    campaignEvent: event.campaignEvent === true,
    statusCounts: aggregateLabSummary(event.labSummary),
    challengeBreakdown: event.labSummary ?? {},
  }));
};

/** Status keys excluded from the modal table (inferred metadata, not realtime status) */
const EXCLUDED_STATUSES = new Set(['WAS_EVER_ASSIGNED', 'WASTED']);

/**
 * Collects all unique status keys across all event rows and returns them
 * sorted alphabetically. Excludes inferred metadata statuses.
 * Used to determine which columns to show in the table.
 */
export const getStatusColumns = (rows: AggregatedEventRow[]): string[] => {
  const statusSet = new Set<string>();

  for (const row of rows) {
    for (const status of Object.keys(row.statusCounts)) {
      if (!EXCLUDED_STATUSES.has(status)) {
        statusSet.add(status);
      }
    }
  }

  return Array.from(statusSet).sort();
};
